import { ModelDisplay } from '@components/shared/form-suggestion.component';
import { Button } from '@components/ui/button';
import { Textarea } from '@components/ui/textarea';
import * as Portal from '@radix-ui/react-portal';
import { CheckIcon, Loader2Icon, MessageSquarePlusIcon } from 'lucide-react';
import { type FC, type ReactNode, memo, useEffect, useMemo, useRef, useState } from 'react';
import type { LlmChatSuggestion } from './llm-chat.util';

const formatDataLabel = (key: string): string =>
  key
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .replace(/^./, (char) => char.toUpperCase());

const DataArtifactValue: FC<{ value: unknown }> = (props) => {
  const { value } = props;

  if (value === null || value === undefined || value === '') {
    return <span className="italic text-gray-400">Not provided</span>;
  }

  if (typeof value === 'string') {
    return <span className="whitespace-pre-wrap">{value}</span>;
  }

  if (typeof value === 'number') {
    return <span className="font-medium">{value}</span>;
  }

  if (typeof value === 'boolean') {
    return <span className="font-medium">{value ? 'Yes' : 'No'}</span>;
  }

  if (Array.isArray(value)) {
    if (value.length === 0) {
      return <span className="italic text-gray-400">None</span>;
    }

    if (value.every((entry) => typeof entry === 'string' || typeof entry === 'number')) {
      return <span>{value.join(', ')}</span>;
    }
  }

  return <pre className="whitespace-pre-wrap text-xs text-gray-600">{JSON.stringify(value, null, 2)}</pre>;
};

const DataArtifactView: FC<{ data: Record<string, unknown> }> = memo((props) => {
  const { data } = props;

  return (
    <div className="space-y-2">
      {Object.entries(data).map(([key, value]) => (
        <div
          className="rounded-md border border-gray-200 bg-white p-2"
          key={key}
        >
          <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-500">{formatDataLabel(key)}</div>
          <div className="text-sm text-gray-700">
            <DataArtifactValue value={value} />
          </div>
        </div>
      ))}
    </div>
  );
});
DataArtifactView.displayName = 'DataArtifactView';

type SelectionAnchor = {
  left: number;
  quote: string;
  top: number;
};

type SuggestionArtifactPanelProps = {
  isBusy: boolean;
  onAccept: () => void;
  /**
   * Stage a comment (highlighted excerpt + requested change). Staged comments are
   * attached to the next chat message automatically.
   */
  onAddComment: (comment: { quote: string; text: string }) => void;
  /**
   * Optional surface-specific renderer for structured-object drafts. Return null
   * to fall back to the generic label/value view.
   */
  renderData?: (data: Record<string, unknown>) => ReactNode;
  suggestion?: LlmChatSuggestion;
};

const PILL_EDGE_PX = 64;
const COMPOSER_HALF_WIDTH_PX = 152;
const COMPOSER_APPROX_HEIGHT_PX = 200;

const clamp = (value: number, min: number, max: number): number => Math.min(Math.max(value, min), Math.max(max, min));

/**
 * Selections can span block boundaries (e.g. a field label and its value in the
 * object grid); join the pieces explicitly so the quote stays readable for both
 * the user and the model instead of running together.
 */
const normalizeQuote = (text: string): string =>
  text
    .split('\n')
    .map((line) => line.trim().replace(/\s+/g, ' '))
    .filter(Boolean)
    .join(' — ');

export const SuggestionArtifactPanel: FC<SuggestionArtifactPanelProps> = (props) => {
  const { isBusy, onAccept, onAddComment, renderData, suggestion } = props;
  const canAccept =
    Boolean(suggestion) &&
    !suggestion?.isInitial &&
    !suggestion?.isStreaming &&
    !isBusy &&
    (suggestion?.kind !== 'data' || Boolean(suggestion.data));

  const contentRef = useRef<HTMLDivElement>(null);
  const [selectionAnchor, setSelectionAnchor] = useState<SelectionAnchor | null>(null);
  const [commentAnchor, setCommentAnchor] = useState<SelectionAnchor | null>(null);
  const [commentText, setCommentText] = useState('');

  // Selections and open composers go stale whenever the draft content changes
  // (new version or streaming tokens), so drop them with it.
  const suggestionFingerprint =
    suggestion?.kind === 'text' ? suggestion.content : (suggestion?.data ?? suggestion?.errors);

  // biome-ignore lint/correctness/useExhaustiveDependencies: deps are change-triggers only; the effect resets transient selection/composer state and must re-run when the draft version or content changes.
  useEffect(() => {
    setSelectionAnchor(null);
    setCommentAnchor(null);
    setCommentText('');
  }, [suggestion?.versionId, suggestionFingerprint]);

  // Memoize the object artifact so frequent panel re-renders (selection/comment
  // state) don't re-run the surface renderer or the field grid; it only recomputes
  // when the suggestion or renderer changes.
  const dataNode = useMemo(() => {
    if (suggestion?.kind !== 'data' || !suggestion.data) return null;

    return renderData?.(suggestion.data) ?? <DataArtifactView data={suggestion.data} />;
  }, [renderData, suggestion]);

  const updateSelectionAnchor = () => {
    // While the composer is open, leave the selection flow alone.
    if (commentAnchor) return;

    const container = contentRef.current;
    const selection = window.getSelection();

    if (!container || !selection || selection.isCollapsed || selection.rangeCount === 0) {
      setSelectionAnchor(null);
      return;
    }

    const quote = normalizeQuote(selection.toString());
    const range = selection.getRangeAt(0);

    if (!quote || !container.contains(range.commonAncestorContainer)) {
      setSelectionAnchor(null);
      return;
    }

    // Viewport coordinates: the pill/composer render in a body portal (fixed),
    // so they can never be clipped by the panel's scroll container.
    const rangeRect = range.getBoundingClientRect();

    setSelectionAnchor({
      left: clamp(rangeRect.left + rangeRect.width / 2, PILL_EDGE_PX, window.innerWidth - PILL_EDGE_PX),
      quote,
      top: Math.max(rangeRect.top - 36, 8),
    });
  };

  const openComposer = () => {
    if (!selectionAnchor) return;

    setCommentAnchor({
      left: clamp(selectionAnchor.left, COMPOSER_HALF_WIDTH_PX, window.innerWidth - COMPOSER_HALF_WIDTH_PX),
      quote: selectionAnchor.quote,
      top: clamp(selectionAnchor.top, 8, window.innerHeight - COMPOSER_APPROX_HEIGHT_PX),
    });
    setSelectionAnchor(null);
    setCommentText('');
    window.getSelection()?.removeAllRanges();
  };

  const closeComposer = () => {
    setCommentAnchor(null);
    setCommentText('');
  };

  const submitComment = () => {
    if (!commentAnchor || !commentText.trim()) return;

    onAddComment({ quote: commentAnchor.quote, text: commentText.trim() });
    closeComposer();
  };

  return (
    <div className="flex h-full min-h-0 flex-col rounded-lg border border-gray-200 bg-gray-50">
      <div
        className="relative min-h-0 flex-1 overflow-y-auto p-4"
        onMouseUp={updateSelectionAnchor}
        onKeyUp={updateSelectionAnchor}
        onScroll={() => setSelectionAnchor(null)}
        ref={contentRef}
      >
        {!suggestion && (
          <div className="flex h-full items-center justify-center text-center text-sm text-gray-400">
            Your draft will appear here. Ask for a draft in the chat, then refine it until it is ready to accept.
          </div>
        )}

        {suggestion?.kind === 'text' && (
          <div className="whitespace-pre-wrap text-sm leading-relaxed text-gray-700">{suggestion.content}</div>
        )}

        {suggestion?.kind === 'data' && !suggestion.data && !suggestion.errors && (
          <div className="flex h-full flex-col items-center justify-center gap-2 text-center text-sm text-gray-400">
            <Loader2Icon
              className="animate-spin"
              size={18}
            />
            Drafting the object — it will appear here once it passes validation.
          </div>
        )}

        {suggestion?.kind === 'data' && suggestion.errors && (
          <div className="rounded-md border border-red-200 bg-red-50 p-3 text-xs text-red-700">
            <p className="mb-1 font-medium">The latest draft failed validation — ask the assistant to fix it:</p>
            <ul className="list-inside list-disc space-y-0.5">
              {suggestion.errors.map((error, index) => (
                <li key={`${index}-${error}`}>{error}</li>
              ))}
            </ul>
          </div>
        )}

        {dataNode}
      </div>

      {selectionAnchor && (
        <Portal.Root>
          <div
            className="fixed z-[100] -translate-x-1/2"
            style={{ left: selectionAnchor.left, top: selectionAnchor.top }}
          >
            <Button
              className="shadow-lg hover:bg-button-primary"
              onClick={openComposer}
              onMouseDown={(event) => event.preventDefault()}
              size="xs"
              type="button"
              variant="primary"
            >
              <MessageSquarePlusIcon size={14} />
              Suggest Edit
            </Button>
          </div>
        </Portal.Root>
      )}

      {commentAnchor && (
        <Portal.Root>
          <div
            className="fixed z-[100] w-72 -translate-x-1/2 rounded-lg border border-gray-200 bg-white p-2 shadow-xl"
            style={{ left: commentAnchor.left, top: commentAnchor.top }}
          >
            <div className="mb-2 line-clamp-2 rounded bg-gray-100 px-2 py-1 text-xs italic text-gray-500">
              “{commentAnchor.quote}”
            </div>
            <Textarea
              autoFocus
              className="resize-none text-sm"
              onChange={(event) => setCommentText(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' && !event.shiftKey) {
                  event.preventDefault();
                  submitComment();
                }
                if (event.key === 'Escape') {
                  // Keep the modal's document-level Escape handler out of this.
                  event.stopPropagation();
                  closeComposer();
                }
              }}
              placeholder="Request change"
              rows={2}
              value={commentText}
            />
            <div className="mt-2 flex justify-end gap-2">
              <Button
                onClick={closeComposer}
                size="xs"
                type="button"
                variant="secondary"
              >
                Cancel
              </Button>
              <Button
                disabled={!commentText.trim()}
                onClick={submitComment}
                size="xs"
                type="button"
                variant="primary"
              >
                Suggest
              </Button>
            </div>
          </div>
        </Portal.Root>
      )}

      {/* pb-1 mirrors the chat input's p-1 button row so the Accept button lines
          up vertically with the send button across the split view. */}
      <div className="flex shrink-0 items-center justify-between gap-2 border-t border-gray-200 px-3 pb-1 pt-3">
        <div className="flex items-center gap-2 text-xs font-medium text-gray-500">
          {suggestion?.isStreaming && (
            <span className="flex items-center gap-1.5">
              <Loader2Icon
                className="animate-spin"
                size={14}
              />
              Streaming…
            </span>
          )}
          {suggestion?.model && <ModelDisplay model={suggestion.model} />}
        </div>

        <Button
          disabled={!canAccept}
          onClick={onAccept}
          size="sm"
          type="button"
          variant="primary"
        >
          <CheckIcon size={14} />
          Accept
        </Button>
      </div>
    </div>
  );
};
